import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, cant, fecha, hoy, buscar } from '../utils/formato';

// Entrada de mercancía.
//
// Se teclea lo que dice la factura del proveedor —tres cajas, veinte láminas— y
// el sistema convierte a unidades de venta. Escribir «3000 tornillos» a mano
// cada vez es donde aparece el error de un cero de más.
//
// Al guardar se recalcula el costo promedio de cada producto y se avisa si el
// precio de venta se quedó por debajo. Eso hay que verlo el día de la compra y
// no el día en que se vende perdiendo.

function Entradas({ onCambio }) {
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const [lineas, setLineas] = useState([]);
  const [cabecera, setCabecera] = useState({
    proveedor_id: '', fecha: hoy(), factura: '', forma_pago: 'efectivo',
  });
  const [nuevoProv, setNuevoProv] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [cambios, setCambios] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/inventario').then(r => setProductos(r.data || [])).catch(() => {});
    axios.get('/api/proveedores').then(r => setProveedores(r.data || [])).catch(() => {});
    axios.get('/api/entradas').then(r => setEntradas(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const encontrados = buscar(productos, busqueda).slice(0, busqueda ? 20 : 0);
  const total = lineas.reduce((s, l) => s + (parseFloat(l.costo_total) || 0), 0);

  const agregar = (p) => {
    if (lineas.some(l => l.producto_id === p.id)) return;
    setLineas([...lineas, {
      clave: Date.now(),
      producto_id: p.id,
      nombre: p.nombre,
      codigo: p.codigo,
      unidad: p.unidad,
      nombre_empaque: p.nombre_empaque,
      por_empaque: p.unidades_por_empaque,
      empaques: '',
      costo_total: '',
    }]);
    setBusqueda('');
  };

  const cambiar = (clave, campo, valor) =>
    setLineas(lineas.map(l => l.clave === clave ? { ...l, [campo]: valor } : l));

  const quitar = (clave) => setLineas(lineas.filter(l => l.clave !== clave));

  // Anular una entrada mal tecleada. Es el error más caro de la pantalla: un
  // cero de más en «cuántos llegaron» descuadra la existencia Y el costo
  // promedio, y el costo promedio no se arregla con un ajuste de inventario.
  const anular = async (entrada) => {
    const cuantos = entrada.lineas.map(l => `${l.producto} (${cant(l.unidades, l.unidad)})`).join(', ');
    if (!window.confirm(
      `¿Anular la entrada de ${plata(entrada.total)}?\n\n${cuantos}\n\n` +
      'Se devuelve el inventario y se vuelve a calcular el costo de esos productos.')) return;
    try {
      await axios.post(`/api/entradas/${entrada.id}/anular`);
      setAviso({ tipo: 'bueno', texto: 'Entrada anulada. El costo de esos productos quedó recalculado.' });
      setCambios(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo anular' });
    }
  };

  const crearProveedor = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post('/api/proveedores', nuevoProv);
      setNuevoProv(null);
      const r = await axios.get('/api/proveedores');
      setProveedores(r.data || []);
      setCabecera({ ...cabecera, proveedor_id: String(data.id) });
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el proveedor');
    }
  };

  const guardar = async () => {
    if (lineas.length === 0) return;
    const malas = lineas.filter(l =>
      !(parseFloat(l.empaques) > 0) || !(parseFloat(l.costo_total) > 0));
    if (malas.length > 0) {
      return setAviso({ tipo: 'malo',
        texto: `Falta la cantidad o el costo en ${malas.length} ${malas.length === 1 ? 'línea' : 'líneas'}` });
    }

    setGuardando(true);
    setAviso(null);
    setCambios(null);
    try {
      const { data } = await axios.post('/api/entradas', {
        ...cabecera,
        proveedor_id: cabecera.proveedor_id ? parseInt(cabecera.proveedor_id) : null,
        lineas: lineas.map(l => ({
          producto_id: l.producto_id,
          empaques: parseFloat(l.empaques),
          costo_total: parseFloat(l.costo_total),
        })),
      });
      setAviso({ tipo: 'bueno', texto: `Entrada de ${plata(data.total)} registrada` });
      setCambios(data.cambios || []);
      setLineas([]);
      setCabecera({ proveedor_id: '', fecha: hoy(), factura: '', forma_pago: 'efectivo' });
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  return (
    <>
      <div className="encabezado">
        <h1>Entrada de mercancía</h1>
        <p>Lo que llega del proveedor, tal como viene en la factura</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {/* Cómo quedó el costo de cada producto tras la compra */}
      {cambios && cambios.length > 0 && (
        <div className="bloque">
          <h2>📊 Cómo quedaron los costos</h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Entraron</th>
                  <th style={{ textAlign: 'right' }}>Costaba</th>
                  <th style={{ textAlign: 'right' }}>Cuesta ahora</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cambios.map((c, i) => {
                  const subio = c.costo_ahora > c.costo_antes && c.costo_antes > 0;
                  return (
                    <tr key={i}>
                      <td className="recio">{c.producto}</td>
                      <td className="num">{cant(c.unidades)}</td>
                      <td className="num">{c.costo_antes > 0 ? plata(c.costo_antes) : '—'}</td>
                      <td className="num recio"
                          style={{ color: subio ? 'var(--ojo)' : undefined }}>
                        {plata(c.costo_ahora)}
                        {subio && (
                          <div style={{ fontSize: 11, fontWeight: 400 }}>
                            +{((c.costo_ahora - c.costo_antes) / c.costo_antes * 100).toFixed(0)}%
                          </div>
                        )}
                      </td>
                      <td>
                        {c.precio_quedo_corto && (
                          <span className="pinta malo">
                            ⚠️ se vende a {plata(c.precio_venta)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {cambios.some(c => c.precio_quedo_corto) && (
            <div className="cuerpo" style={{ paddingTop: 12 }}>
              <div className="aviso malo" style={{ marginBottom: 0 }}>
                <strong>Hay productos que ahora cuestan más de lo que se venden.</strong>{' '}
                Súbeles el precio en la pantalla de Productos antes de seguir vendiendo,
                o cada venta va a perder plata.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bloque">
        <h2>🚚 Nueva entrada</h2>
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Proveedor</span>
              <select value={cabecera.proveedor_id}
                      onChange={e => setCabecera({ ...cabecera, proveedor_id: e.target.value })}>
                <option value="">— Elegir —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </label>
            <label className="campo">
              <span>Factura</span>
              <input value={cabecera.factura} placeholder="Número"
                     onChange={e => setCabecera({ ...cabecera, factura: e.target.value })} />
            </label>
            <label className="campo">
              <span>Fecha</span>
              <input type="date" value={cabecera.fecha}
                     onChange={e => setCabecera({ ...cabecera, fecha: e.target.value })} />
            </label>
            <label className="campo">
              <span>Cómo se pagó</span>
              <select value={cabecera.forma_pago}
                      onChange={e => setCabecera({ ...cabecera, forma_pago: e.target.value })}>
                <option value="efectivo">💵 Efectivo</option>
                <option value="transferencia">🏦 Transferencia</option>
                <option value="credito">📝 A crédito</option>
              </select>
            </label>
          </div>

          {proveedores.length === 0 && !nuevoProv && (
            <div className="acciones">
              <button className="boton suave chico"
                      onClick={() => setNuevoProv({ nombre: '', telefono: '' })}>
                ➕ Agregar proveedor
              </button>
            </div>
          )}

          {nuevoProv && (
            <form onSubmit={crearProveedor} style={{ marginTop: 14 }}>
              <div className="campos">
                <label className="campo">
                  <span>Nombre del proveedor *</span>
                  <input value={nuevoProv.nombre} required autoFocus
                         onChange={e => setNuevoProv({ ...nuevoProv, nombre: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Teléfono</span>
                  <input value={nuevoProv.telefono}
                         onChange={e => setNuevoProv({ ...nuevoProv, telefono: e.target.value })} />
                </label>
              </div>
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setNuevoProv(null)}>
                  Cancelar
                </button>
                <button className="boton chico">💾 Guardar proveedor</button>
              </div>
            </form>
          )}

          <div className="buscador" style={{ marginTop: 16 }}>
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                   placeholder="🔎 Buscar el producto que llegó" />
            {busqueda && <button type="button" onClick={() => setBusqueda('')}>×</button>}
          </div>

          {encontrados.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginBottom: 14,
                          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
              {encontrados.map(p => (
                <button key={p.id} onClick={() => agregar(p)}
                        style={{ padding: '10px 12px', textAlign: 'left', cursor: 'pointer',
                                 borderRadius: 8, font: 'inherit',
                                 border: '1px solid var(--raya)', background: 'var(--tarjeta)' }}>
                  <div className="cifra" style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                    {p.codigo}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{p.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                    {p.unidades_por_empaque > 1
                      ? `${p.nombre_empaque} de ${cant(p.unidades_por_empaque)} ${p.unidad}`
                      : `por ${p.unidad}`}
                  </div>
                </button>
              ))}
            </div>
          )}

          {lineas.length === 0 ? (
            <div className="vacio">
              Busca arriba los productos que llegaron y tócalos para agregarlos.
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Cuántos llegaron</th>
                    <th style={{ textAlign: 'right' }}>= unidades</th>
                    <th style={{ textAlign: 'right' }}>Costó (total)</th>
                    <th style={{ textAlign: 'right' }}>Por unidad</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map(l => {
                    const emp = parseFloat(l.empaques) || 0;
                    const unidades = emp * l.por_empaque;
                    const costo = parseFloat(l.costo_total) || 0;
                    return (
                      <tr key={l.clave}>
                        <td className="recio">
                          {l.nombre}
                          <div className="cifra" style={{ fontSize: 11, fontWeight: 400,
                                                          color: 'var(--tinta-tenue)' }}>
                            {l.codigo}
                          </div>
                        </td>
                        <td style={{ minWidth: 150 }}>
                          <input type="number" step="0.01" min="0" className="num-grande"
                                 value={l.empaques} placeholder="0"
                                 onChange={e => cambiar(l.clave, 'empaques', e.target.value)} />
                          <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3,
                                        textAlign: 'right' }}>
                            {l.por_empaque > 1 ? l.nombre_empaque + 's' : l.unidad}
                          </div>
                        </td>
                        <td className="num recio">
                          {unidades > 0 ? cant(unidades, l.unidad) : '—'}
                        </td>
                        <td style={{ minWidth: 140 }}>
                          <input type="number" step="1" min="0" className="num-grande"
                                 value={l.costo_total} placeholder="0"
                                 onChange={e => cambiar(l.clave, 'costo_total', e.target.value)} />
                        </td>
                        <td className="num">
                          {unidades > 0 && costo > 0 ? plata(costo / unidades) : '—'}
                        </td>
                        <td>
                          <button className="boton chico suave"
                                  onClick={() => quitar(l.clave)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {lineas.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                          gap: 16, flexWrap: 'wrap', marginTop: 18 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>Total de la factura</div>
                <div className="cifra" style={{ fontSize: 28, fontWeight: 800,
                                                letterSpacing: '-.03em', color: 'var(--acero)' }}>
                  {plata(total)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="boton suave" onClick={() => setLineas([])}>Vaciar</button>
                <button className="boton grande" disabled={guardando} onClick={guardar}>
                  {guardando ? 'Guardando…' : '📥 Registrar la entrada'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {entradas.length > 0 && (
        <div className="bloque">
          <h2>📋 Entradas anteriores <span className="contador">{entradas.length}</span></h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Proveedor</th>
                  <th>Factura</th>
                  <th>Qué llegó</th>
                  <th>Pago</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entradas.slice(0, 30).map(e => (
                  <tr key={e.id} style={{ opacity: e.anulada ? 0.45 : 1 }}>
                    <td>{fecha(e.fecha)}</td>
                    <td>{e.proveedor || '—'}</td>
                    <td className="codigo">{e.factura || '—'}</td>
                    <td style={{ fontSize: 13,
                                 textDecoration: e.anulada ? 'line-through' : 'none' }}>
                      {e.lineas.map(l => (
                        <div key={l.id}>{l.producto} · {cant(l.unidades, l.unidad)}</div>
                      ))}
                    </td>
                    <td>
                      <span className={'pinta ' + (e.forma_pago === 'efectivo' ? 'bueno'
                        : e.forma_pago === 'transferencia' ? 'acero' : 'ojo')}>
                        {e.forma_pago}
                      </span>
                    </td>
                    <td className="num recio">{plata(e.total)}</td>
                    <td>
                      {e.anulada
                        ? <span className="pinta malo">anulada</span>
                        : <button className="boton chico suave" onClick={() => anular(e)}>
                            Anular
                          </button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default Entradas;
